import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bd_admin_token";

const AuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(TOKEN_KEY) || null;
    } catch (_) {
      return null;
    }
  });
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(!!token);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch (_) {
      // ignore
    }
    setToken(null);
    setAdmin(null);
  }, []);

  // Validate session on mount / when token changes
  useEffect(() => {
    let active = true;
    if (!token) {
      setAdmin(null);
      setLoading(false);
      return () => {
        active = false;
      };
    }
    setLoading(true);
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (active) setAdmin(r.data);
      })
      .catch(() => {
        if (active) {
          try {
            localStorage.removeItem(TOKEN_KEY);
          } catch (_) {
            // ignore
          }
          setToken(null);
          setAdmin(null);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/admin/login`, { email, password });
    const t = res.data?.token;
    if (!t) throw new Error("Réponse invalide du serveur");
    try {
      localStorage.setItem(TOKEN_KEY, t);
    } catch (_) {
      // ignore
    }
    setToken(t);
    return t;
  }, []);

  const value = useMemo(
    () => ({
      token,
      admin,
      loading,
      isAuthenticated: !!admin,
      login,
      logout,
    }),
    [token, admin, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}

export function adminAxios(token) {
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}
